// src\contexts\ReportBuilderContext.jsx
import { WIDGET_TYPES } from "../components/report-builder/types/index.js";
import {
  buildMetricsTreeForContext,
  buildDimensionOptionsForMetrics,
  getMetricBaseDateDimension,
  getDimensionByIdentifier,
  getMetricByIdentifier,
} from "../components/report-builder/utils/builderMetricList";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import { v4 as uuidv4 } from "uuid";
import ReportBuilderService from "../services/reportBuilderService";
import dayjs from "dayjs";
import { useAuth } from "../hooks";
import ReportService from "../services/reportService";
import { Toast } from "../components/ui";
import isEqual from 'lodash/isEqual';
import metricList from "../components/report-builder/utils/metricList.json";
import { sanitizeReportSchemaForRuntime } from "../components/report-builder/utils/sanitizeReportSchema.js";
import { buildMergedDimensionFiltersPayload } from "../helpers/reportBuilderContextHelper.js";
import { ERPS_COM_CATEGORIA_SUPERIOR } from "../components/report-builder/widgets/configs/FluxoCaixaConfig.jsx";

//==============================
// Seção de filtro de dimensão
//==============================
export const DIMENSION_FILTERS = {
  CATEGORIA: 0,
  DEPARTAMENTO: 1,
  CONTA: 2,
  PROJETO: 3,
  /** Empresa (syncs dependentes): apenas para sync type=2 (consolidado) */
  EMPRESA: 4,
  /** Pessoa (cliente/fornecedor): disponível para métricas financeiras e de vendas */
  PESSOA: 5,
  /** Produto/Serviço: disponível apenas para métricas de Vendas (não suportado no Nibo) */
  PRODUTO_SERVICO: 6,
};
//==============================
// Fim da seção de filtro de dimensão
//==============================

export const ReportBuilderContext = createContext();

const DEFAULT_COLOR_PALETTE = [
  '#1677ff', '#69b1ff',
  '#52c41a', '#95de64',
  '#ff4d4f', '#ff7875',
  '#fa8c16', '#ffc069',
  '#722ed1', '#b37feb',
];

const METRIC_READ_ONLY_MAP = Object.fromEntries(
  (metricList || []).map(m => [m.identifier, !!m.read_only])
);

const METRIC_COLOR_MAP = Object.fromEntries(
  (metricList || []).map(m => [m.identifier, m.color])
);

function applyDefaultColorsToWidgets(widgets) {
  if (!Array.isArray(widgets)) return widgets;
  return widgets.map((w) => {
    const cloned = { ...w };
    if (Array.isArray(cloned.metrics)) {
      cloned.metrics = cloned.metrics.map((m, idx) => ({
        ...m,
        color: m?.color || METRIC_COLOR_MAP[m?.metricIdentifier] || DEFAULT_COLOR_PALETTE[idx % DEFAULT_COLOR_PALETTE.length],
        read_only: m?.read_only ?? METRIC_READ_ONLY_MAP[m?.metricIdentifier] ?? false,
      }));
    }
    return cloned;
  });
}

function normalizeDateStr(value) {
  if (!value) return undefined;
  const d = dayjs(value);
  return d.isValid() ? d.format('YYYY-MM-DD') : undefined;
}

function normalizeRange(range) {
  if (!range) return {};
  const start = normalizeDateStr(range.start || range.from);
  const end = normalizeDateStr(range.end || range.to);
  const result = {};
  if (start) result.start = start;
  if (end) result.end = end;
  return result;
}

function normalizeRegimeValue(value) {
  const normalized = String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

  if (normalized === "pagamento") return "pagamento";
  if (normalized === "emissao") return "emissao";
  if (normalized === "vencimento") return "vencimento";
  if (normalized === "previsao") return "previsao";
  if (normalized === "faturamento") return "faturamento";
  return null;
}

function getEffectiveMetricRegime(metricIdentifier, selectedRegime) {
  const metric = getMetricByIdentifier(Number(metricIdentifier));
  const baseDateRegime = metric?.group !== 3
    ? normalizeRegimeValue(metric?.api_base_date || metric?.base_date)
    : null;

  return baseDateRegime || normalizeRegimeValue(selectedRegime) || selectedRegime;
}

function buildWidgetQueryConfigSignature(widget) {
  const config = widget?.config || {};
  switch (widget?.type) {
    case WIDGET_TYPES.DRE:
      return {
        regimeType: config.regimeType || "emissao",
        dreGroupBy: typeof config.dreGroupBy === "number" ? config.dreGroupBy : 0,
        analysisHorizontal: !!config?.analysis?.horizontal,
      };
    case WIDGET_TYPES.DFC:
      return {
        regimeType: config.regimeType || "pagamento",
      };
    case WIDGET_TYPES.FLUXO_CAIXA:
      return {
        regimeType: config.regimeType || "pagamento",
        usaSaldo: !!config.usaSaldo,
      };
    case WIDGET_TYPES.SALDOS:
      return {
        daysForward: Number(config.daysForward || 30),
        includeSolvency: !!config.includeSolvency,
        dateBase: config.dateBase || "vencimento",
        status: Array.isArray(config.status) ? config.status : [],
        viewMode: config.viewMode || "table",
      };
    default:
      return {};
  }
}

function buildWidgetQuerySignature(widget) {
  if (!widget) return null;
  return {
    type: widget.type,
    metrics: (widget.metrics || []).map((m) => ({
      metricIdentifier: m?.metricIdentifier ?? null,
      regime: m?.regime ?? null,
    })),
    dimensions: (widget.dimensions || []).map((d) => ({
      dimensionIdentifier: d?.dimensionIdentifier ?? null,
    })),
    dimFilters: widget.dimFilters || {},
    dateFilter: widget.dateFilter || {},
    useComparison: !!widget.useComparison,
    groupSameLabels: widget.groupSameLabels !== false,
    groups: widget.groups,
    cutA: widget.cutA,
    cutB: widget.cutB,
    ranges: widget.ranges || [],
    queryConfig: buildWidgetQueryConfigSignature(widget),
  };
}

export const ReportBuilderProvider = ({ children }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [template, setTemplate] = useState(null);
  const [metrics, setMetrics] = useState([]);
  const [dimensions, setDimensions] = useState([]);
  const [sync, setSync] = useState(null);
  const [layout, setLayout] = useState([]);
  const [widgets, setWidgets] = useState([]);
  const [filters, setFilters] = useState({
    start: dayjs().subtract(1, "year").format('YYYY-MM-DD'),
    end: dayjs().format('YYYY-MM-DD'),
  });
  const [dimensionFilter, setDimensionFilter] = useState({});
  const [comparison, setComparison] = useState({ start: null, end: null });
  const [editingWidget, setEditingWidget] = useState(null);
  const [filteringWidget, setFilteringWidget] = useState(null);
  const [isPreview, setIsPreview] = useState(false);
  const [title, setTitle] = useState("Novo Relatório");
  const [description, setDescription] = useState("");
  
  const [widgetData, setWidgetData] = useState([]);
  const [reportId, setReportId] = useState(null);
  const [reportType, setReportType] = useState('None');
  const [savedSnapshot, setSavedSnapshot] = useState(null);
  const [useComparisonMode, setUseComparisonMode] = useState(false);
  const [readOnly, setReadOnly] = useState(false);
  const [gridHeight, setGridHeight] = useState('');
  const [dimensionValuesCache, setDimensionValuesCache] = useState({});
  const desiredHeightsRef = useRef({});

  const getCurrentSnapshot = useCallback(() => ({
    layout,
    widgets,
    filters,
    comparison,
    title,
    description,
    useComparisonMode,
    readOnly,
  }), [layout, widgets, filters, comparison, title, description, useComparisonMode, readOnly]);

  const isDirty = useMemo(() => {
    // Se não há snapshot salvo, não há mudanças para detectar
    if (!savedSnapshot) return false;
    
    // Se não há dados carregados (layout e widgets vazios), não há mudanças
    if (layout.length === 0 && widgets.length === 0) return false;
    
    return !isEqual(getCurrentSnapshot(), savedSnapshot);
  }, [getCurrentSnapshot, savedSnapshot, layout.length, widgets.length]);

  const markSaved = useCallback(() => {
    setSavedSnapshot(getCurrentSnapshot());
  }, [getCurrentSnapshot]);

  // Não inicializa savedSnapshot automaticamente; ele será definido após carregar schema/template/reset/save
  // O savedSnapshot só deve ser definido quando realmente houver dados carregados

  // Quando habilitar o modo comparação, preencher comparação padrão apenas se ainda não existir
  useEffect(() => {
    if (!useComparisonMode) return;
    if (
      filters?.start &&
      filters?.end &&
      (!comparison?.start || !comparison?.end)
    ) {
      const nextComparison = {
        start: dayjs(filters.start).subtract(1, 'year').format('YYYY-MM-DD'),
        end: dayjs(filters.end).subtract(1, 'year').format('YYYY-MM-DD'),
      };
      setComparison((prev) => isEqual(prev, nextComparison) ? prev : nextComparison);
    }
  }, [useComparisonMode, filters?.start, filters?.end, comparison?.start, comparison?.end]);

  // Se desabilitar o modo de comparação global, desabilita a comparação em todos os widgets
  useEffect(() => {
    if (useComparisonMode) return;

    setWidgets((prevWidgets) => {
      const needsUpdate = prevWidgets.some(
        (w) => w.useComparison === true || w.dateFilter?.useComparison === true
      );

      if (!needsUpdate) return prevWidgets;

      return prevWidgets.map((w) => {
        const updatedDateFilter = w.dateFilter ? { ...w.dateFilter, useComparison: false } : w.dateFilter;
        return {
          ...w,
          useComparison: false,
          dateFilter: updatedDateFilter
        };
      });
    });
  }, [useComparisonMode]);



  //Se em edição terá ReportId e deverá carregar o schema
  useEffect(() => {
    const loadSchema = async () => {
      setLoading(true);
      try {
        const schemaResponse = await ReportService.getSchema(reportId);
        if (schemaResponse.status === 200) {
          let schemaContainer = schemaResponse.data.response || {};
          
          // Se for um array (novo formato de páginas), pega o primeiro item por enquanto
            if (Array.isArray(schemaContainer)) {
              schemaContainer = schemaContainer[0] || {};
            }

            let schema = schemaContainer.schema;
            
            // Se o schema for um array (lista de páginas), pega a primeira página
            if (Array.isArray(schema)) {
              schema = schema[0] || {};
            }

            if (schema) {
            schema = sanitizeReportSchemaForRuntime(schema);
            const nextFilters = normalizeRange(schema.filters || {});
            const incomingComparison = schema.comparision || schema.comparison || {};
            const baseComparison = normalizeRange(incomingComparison);
            let nextUseComparisonMode = !!schema.useComparisonMode;

            // Se a comparação já vier preenchida no schema, força o modo de comparação ativo
            if (baseComparison?.start && baseComparison?.end) {
              nextUseComparisonMode = true;
            }

            const nextReadOnly = schemaContainer.readOnly || false;
            const sanitizedWidgets = applyDefaultColorsToWidgets(schema.widgets || []);
            const nextLayout = schema.layout || [];
            const nextDimensionFilter = schema.dimensionFilter || {}

            // Preencher comparação padrão se necessário
            let finalComparison = baseComparison;
            if (
              nextUseComparisonMode &&
              nextFilters?.start &&
              nextFilters?.end &&
              (!finalComparison?.start || !finalComparison?.end)
            ) {
              finalComparison = {
                start: dayjs(nextFilters.start).subtract(1, 'year').format('YYYY-MM-DD'),
                end: dayjs(nextFilters.end).subtract(1, 'year').format('YYYY-MM-DD'),
              };
            }

            // Aplicar estados
            setWidgetData([]);
            setFilters(nextFilters);
            setComparison(finalComparison);
            setUseComparisonMode(nextUseComparisonMode);
            setReadOnly(nextReadOnly);
            setLayout(nextLayout);
            setWidgets(sanitizedWidgets);
            setDimensionFilter(nextDimensionFilter);

            // Snapshot salvo consistente com o que foi carregado
            // Só define o snapshot se realmente houver dados carregados
            if (nextLayout.length > 0 || sanitizedWidgets.length > 0) {
              setSavedSnapshot({
                layout: nextLayout,
                widgets: sanitizedWidgets,
                filters: nextFilters,
                comparison: finalComparison,
                title: schemaContainer.title || title,
                description: schemaContainer.description || description,
                useComparisonMode: nextUseComparisonMode,
                readOnly: nextReadOnly,
              });
            }
          }
        }
      } catch (error) {
        console.error("Erro ao carregar schema:", error);
      } finally {
        setLoading(false);
      }
    };
    if (!reportId) return;

    loadSchema();
  }, [reportId]);

  //Função para carregar template JSON
  const loadTemplate = useCallback((json) => {
    // Garantir que temos um objeto válido
    let templateData = json;
    if (typeof json === "string") {
      try {
        templateData = JSON.parse(json);
      } catch (e) {
        console.error("❌ Erro ao parsear JSON string:", e);
        return;
      }
    }

    // Se templateData for array (formato novo de páginas), pega o primeiro
    if (Array.isArray(templateData)) {
       templateData = templateData[0] || {};
    }

    if (!templateData || typeof templateData !== "object") {
      console.error("❌ Template inválido:", templateData);
      return;
    }

    templateData = sanitizeReportSchemaForRuntime(templateData);
    
    const nextTitle = templateData.title || 'Novo Relatório';
    const nextDescription = templateData.description || '';
    const nextFilters = normalizeRange(templateData.filters || {});
    const nextUseComparisonMode = !!templateData.useComparisonMode;
    // Permitir readOnly no nível raiz do template (fora de schema) também
    const nextReadOnly = templateData.readOnly || false;
    const incomingComparison = templateData.comparision || templateData.comparison || {};
    const baseComparison = normalizeRange(incomingComparison);
    const sanitizedWidgets = applyDefaultColorsToWidgets(templateData.widgets || []);
    const nextLayout = templateData.layout || [];

    let finalComparison = baseComparison;
    if (
      nextUseComparisonMode &&
      nextFilters?.start &&
      nextFilters?.end &&
      (!finalComparison?.start || !finalComparison?.end)
    ) {
      finalComparison = {
        start: dayjs(nextFilters.start).subtract(1, 'year').format('YYYY-MM-DD'),
        end: dayjs(nextFilters.end).subtract(1, 'year').format('YYYY-MM-DD'),
      };
    }

    // Aplicar estados
    setTitle(nextTitle);
    setDescription(nextDescription);
    setFilters(nextFilters);
    setReadOnly(nextReadOnly);
    setComparison(finalComparison);
    setUseComparisonMode(nextUseComparisonMode);
    setLayout(nextLayout);
    setWidgetData([]);
    setWidgets(sanitizedWidgets);

    // Snapshot salvo com o que foi carregado do template
    // Só define o snapshot se realmente houver dados carregados
    if (nextLayout.length > 0 || sanitizedWidgets.length > 0) {
      setSavedSnapshot({
        layout: nextLayout,
        widgets: sanitizedWidgets,
        filters: nextFilters,
        comparison: finalComparison,
        title: nextTitle,
        description: nextDescription,
        useComparisonMode: nextUseComparisonMode,
        readOnly: nextReadOnly,
      });
    }
  }, []);

  //Se for um relatório novo e houver um template sendo aplicado
  useEffect(() => {
    if (reportId) return;

    if (template) {
      setLoading(true);
      loadTemplate(template);
      setLoading(false);
    }
  }, [template, reportId, loadTemplate]);

  //Mudança de Sync requer recarga de metricas
  const loadMetricsAndDimensions = useCallback(() => {
    setLoading(true);
    const erpId = sync?.erp ?? 0;
    const metricsTree = buildMetricsTreeForContext(erpId);

    setMetrics(metricsTree);
    setDimensions([]);
    setLoading(false);
  }, [sync?.erp]);

  useEffect(() => {
    if (!sync?.id) return;
    loadMetricsAndDimensions();
  }, [sync, loadMetricsAndDimensions]);

  useEffect(() => {
    setDimensionValuesCache({});
  }, [sync?.erp, sync?.externalId]);


  const updateWidget = useCallback((options = {}) => {
    if (!editingWidget) return;
    const shouldForceRefresh = options?.forceRefresh === true;
    const id = editingWidget.id;
    const currentWidget = widgets.find((w) => w.id === id);
    if (!currentWidget) return;
    const mergedWidget = { ...currentWidget, ...editingWidget };
    const hasMaterialChange = !isEqual(
      buildWidgetQuerySignature(currentWidget),
      buildWidgetQuerySignature(mergedWidget)
    );
    const shouldInvalidate = shouldForceRefresh || hasMaterialChange;
    const refreshNonce = shouldInvalidate ? `${Date.now()}-${Math.random().toString(36).slice(2, 8)}` : currentWidget.refreshNonce;

    setWidgets((prev) =>
      prev.map((w) => {
        if (w.id !== id) return w;
        return shouldInvalidate
          ? { ...mergedWidget, refreshNonce }
          : mergedWidget;
      })
    );
    if (shouldInvalidate) {
      setWidgetData((prev) => prev.filter((item) => item.id !== id));
    }
  }, [editingWidget, widgets]);

  const updateFilteringWidget = useCallback((options = {}) => {
    if (!filteringWidget) return;
    const shouldForceRefresh = options?.forceRefresh === true;
    const id = filteringWidget.id;
    const currentWidget = widgets.find((w) => w.id === id);
    if (!currentWidget) return;
    const mergedWidget = { ...currentWidget, ...filteringWidget };
    const hasMaterialChange = !isEqual(
      buildWidgetQuerySignature(currentWidget),
      buildWidgetQuerySignature(mergedWidget)
    );
    const shouldInvalidate = shouldForceRefresh || hasMaterialChange;
    const refreshNonce = shouldInvalidate ? `${Date.now()}-${Math.random().toString(36).slice(2, 8)}` : currentWidget.refreshNonce;

    setWidgets((prev) =>
      prev.map((w) => {
        if (w.id !== id) return w;
        return shouldInvalidate
          ? { ...mergedWidget, refreshNonce }
          : mergedWidget;
      })
    );
    if (shouldInvalidate) {
      setWidgetData((prev) => prev.filter((item) => item.id !== id));
    }
  }, [filteringWidget, widgets]);

  const addWidget = useCallback((type, initialSize, initialConfig = {}) => {
    const id = uuidv4(); // ID único simples
    const newLayoutItem = {
      i: id,
      x: 0,
      y: Infinity,
      w: initialSize.w || 4,
      h: initialSize.h || 6,
      minW: initialSize.minW || 2,
      minH: initialSize.minH || 2,
    };

    const newWidget = {
      id: id,
      type,
      title: "Novo Componente",
      color: null,
      description: "",
      metrics: [],
      dimensions: [],
      ranges: initialConfig.ranges || [],
      maxValue: 0,
      showTotal: true,
      showSignal: true,
      highlightNegative: false,
      groupSameLabels: true,
    };

    setLayout((prev) => {
      const newLayout = [...prev, newLayoutItem];
      // Se é o primeiro widget, definir o savedSnapshot inicial
      if (prev.length === 0 && !savedSnapshot) {
        setTimeout(() => {
          setSavedSnapshot({
            layout: newLayout,
            widgets: [...widgets, newWidget],
            filters,
            comparison,
            title,
            description,
            useComparisonMode,
            readOnly,
          });
        }, 0);
      }
      return newLayout;
    });
    setWidgets((prev) => [...prev, newWidget]);
    setEditingWidget(newWidget);
  }, [widgets, savedSnapshot, filters, comparison, title, description, useComparisonMode, readOnly]);

  const removeWidget = useCallback((id) => {
    setLayout((prev) => prev.filter((item) => item.i !== id));
    setWidgets((prev) => prev.filter((widget) => widget.id !== id));
    setWidgetData((prev) => prev.filter((data) => data.id !== id));
    setEditingWidget(null);
    setFilteringWidget(null);
  }, []);


  const duplicateWidget = useCallback((id) => {
    let newId = uuidv4();
    // Atenção: duplicateWidget precisa de acesso aos widgets e layout atuais
    // Usamos funcional updates onde possível, mas aqui precisamos encontrar o widget específico
    setWidgets(prevWidgets => {
        const widget = prevWidgets.find((w) => w.id === id);
        if(!widget) return prevWidgets;
        const newWidget = { ...widget, id: newId };
        
        setLayout(prevLayout => {
             const layoutItem = prevLayout.find((l) => l.i === id);
             if(!layoutItem) return prevLayout;
             return [...prevLayout, { ...layoutItem, i: newWidget.id, y: Infinity }];
        });
        
        setEditingWidget(newWidget);
        return [...prevWidgets, newWidget];
    });
  }, []);

  const saveReport = useCallback(async () => {
    const runtimeSchema = sanitizeReportSchemaForRuntime({
      widgets,
      layout,
      filters,
      comparison,
      useComparisonMode,
      dimensionFilter: dimensionFilter
    });

    const data = {
      id: reportId,
      name: title,
      description: description,
      schema: [runtimeSchema],
      readOnly: readOnly,
      syncId: sync.id,
      projectId: sync.projectId,
      tenantId: user?.tenantId,
      type: reportType,
    };
    try {
      // console.log("📊 Dados para salvar:", data);
      const resp = await ReportService.upsert(data)
      const saved = 
        resp?.data?.response ??
        resp?.response ?? 
        resp;

      const newId = saved?.id;

      if(newId) {
        setReportId(prev => prev && prev === newId ? prev : newId);
      }
      Toast.success('Relatório salvo com sucesso!');

      // Snapshot do que acabamos de persistir
      setSavedSnapshot({
        layout,
        widgets,
        filters,
        comparison,
        title,
        description,
        useComparisonMode,
        readOnly,
      });

      return newId;
    } catch (err) {
      console.error('❌ Erro ao salvar relatório:', err);
      Toast.error('Erro ao salvar relatório');
      throw err;
    }
  }, [reportId, title, description, widgets, layout, filters, comparison, useComparisonMode, dimensionFilter, readOnly, sync, user?.tenantId, reportType]);

  const updateWidgetData = useCallback((widgetId, data) => {
    setWidgetData(prev => {
        const idx = prev.findIndex(w => w.id === widgetId);
        if (idx === -1) return [...prev, { id: widgetId, data }];
        const newArr = [...prev];
        newArr[idx] = { ...newArr[idx], data };
        return newArr;
    });
  }, []);

  const resetState = useCallback(() => {
    setLayout([]);
    setWidgets([]);
    setWidgetData([]);
    setEditingWidget(null);
    setFilteringWidget(null);
    setTitle("Novo Relatório");
    setDescription("");
    setTemplate(null);
    setFilters({
       start: dayjs().subtract(1, "year").format('YYYY-MM-DD'),
       end: dayjs().format('YYYY-MM-DD')
    });
    setComparison({});
    setSync({});
    setIsPreview(false);
    setMetrics([]);
    setReadOnly(false);
    setDimensions([]);
    setReportId(null);
    setReportType(null);
    setUseComparisonMode(false);
    
    // Reset do savedSnapshot para null - será definido apenas quando houver dados carregados
    setSavedSnapshot(null);
  }, []);

  const getDimensionOptionsForMetrics = useCallback(
     (metricIdentifiers, isDate = undefined) => {
       const erpId = sync?.erp ?? 0;
       return buildDimensionOptionsForMetrics(metricIdentifiers, erpId, {
         isDate,
         syncType: sync?.type,
       });
     },
     [sync?.erp, sync?.type]
  );

  const calculateGridHeight = useCallback((manualHeight = null, widgetId = null) => {
    const ROW_HEIGHT = 30; // altura de linha usada para cálculo do grid total
    const TYPE_ROW_HEIGHT = 38; // altura por unidade para widgets de tabela específicos

    // Configurações específicas por tipo de widget
    const typeConfig = {
      dreTable: { maxHeight: 1200 },
      fluxoCaixa: { maxHeight: 1200 },
      saldos: { maxHeight: 1200 },
      matrix: { maxHeight: 1200 },
      dfcTable: { maxHeight: 1200 },
    };

    let currentLayout = layout;

    // Se manualHeight e widgetId são fornecidos, ajusta o widget específico e armazena altura desejada
    if (manualHeight && widgetId) {
      desiredHeightsRef.current[widgetId] = manualHeight;
      const widget = widgets.find(w => w.id === widgetId);
      if (widget && typeConfig[widget.type]) {
        const layoutItem = layout.find(item => item.i === widgetId);
        if (layoutItem) {
          const config = typeConfig[widget.type];
          const limitedHeight = Math.min(manualHeight, config.maxHeight);
          const gridUnits = Math.ceil(limitedHeight / TYPE_ROW_HEIGHT);

          if (layoutItem.h !== gridUnits) {
            currentLayout = layout.map(item => {
              if (item.i === widgetId) {
                return { ...item, h: gridUnits };
              }
              return item;
            });
            setLayout(currentLayout);
          }
        }
      } else {
        // Fallback para tipos não específicos
        const layoutItem = layout.find(item => item.i === widgetId);
        if (layoutItem) {
          const gridUnits = Math.ceil(manualHeight / ROW_HEIGHT);
          if (layoutItem.h !== gridUnits) {
            currentLayout = layout.map(item => (
              item.i === widgetId ? { ...item, h: gridUnits } : item
            ));
            setLayout(currentLayout);
          }
        }
      }
    }

    // Se manualHeight é fornecido sem widgetId, define altura manual total
    if (manualHeight && !widgetId) {
      setGridHeight(manualHeight);
      return;
    }

    // Normaliza todos os widgets com base nas últimas alturas desejadas conhecidas
    const normalizedLayout = currentLayout.map(item => {
      const hpx = desiredHeightsRef.current[item.i];
      if (Number.isFinite(hpx) && hpx > 0) {
        const widget = widgets.find(w => w.id === item.i);
        if (widget?.autoResizeOnExpand === false && widget && typeConfig[widget.type]) {
          return item;
        }
        let units;
        if (widget && typeConfig[widget.type]) {
          const config = typeConfig[widget.type];
          const limitedHeight = Math.min(hpx, config.maxHeight);
          units = Math.ceil(limitedHeight / TYPE_ROW_HEIGHT);
        } else {
          units = Math.ceil(hpx / ROW_HEIGHT);
        }
        if (item.h !== units) return { ...item, h: units };
      }
      return item;
    });

    const hasChanges = normalizedLayout.some((item, i) => item !== currentLayout[i]);
    if (hasChanges) {
      currentLayout = normalizedLayout;
      setLayout(currentLayout);
    }

    // Recalcula altura total do grid baseada no layout atual
    const totalHeight = currentLayout.reduce((maxHeight, item) => {
      const itemBottomPosition = (item.y + item.h) * ROW_HEIGHT + 16; // rowHeight = 30px + margin
      return Math.max(maxHeight, itemBottomPosition);
    }, 0);

    setGridHeight(totalHeight);
  }, [layout, widgets]);

  const makeResponsiveLayouts = useCallback((layout) => {
    const mdCols = 8,
      smCols = 4,
      xsCols = 1;

    const typeMap = {};
    widgets.forEach((w) => {
      typeMap[w.id] = w.type;
    });

    // Função para ordenar o layout preservando a ordem original
    function sortLayoutByOriginalOrder(items) {
      // Primeiro, ordena pela posição Y, depois X para manter a ordem visual
      return [...items].sort((a, b) => {
        if (a.y !== b.y) return a.y - b.y;
        return a.x - b.x;
      });
    }

    function fullPerRow(items, cols) {
      const sortedItems = sortLayoutByOriginalOrder(items);
      return sortedItems.map((l, i) => ({
        ...l,
        x: 0,
        y: i,
        w: cols,
        h: l.h || 6,
      }));
    }

    function twoPerRow(items, cols, fullTypes = []) {
      const sortedItems = sortLayoutByOriginalOrder(items);
      let currentY = 0;
      
      return sortedItems.map((l, i) => {
        const type = typeMap[l.i];
        if (fullTypes.includes(type)) {
          const result = { ...l, x: 0, y: currentY, w: cols, h: l.h || 6 };
          currentY++;
          return result;
        } else {
          const isEven = (i - sortedItems.slice(0, i).filter(item => fullTypes.includes(typeMap[item.i])).length) % 2 === 0;
          const result = {
            ...l,
            x: isEven ? 0 : cols / 2,
            y: currentY,
            w: cols / 2,
            h: l.h || 6,
          };
          if (!isEven) currentY++;
          return result;
        }
      });
    }

    const fullWidthTypes = ["horizontalBar", "verticalBar"];
    return {
      lg: layout,
      md: twoPerRow(layout, mdCols, fullWidthTypes),
      sm: twoPerRow(layout, smCols, fullWidthTypes),
      xs: fullPerRow(layout, xsCols),
    };
  }, [widgets]);

  
  const fetchAPI = useCallback(async (metric, dimensionOrDimensions, useComparison = undefined, widget = undefined, regime = undefined) => {
    if(!widget && editingWidget) widget = editingWidget
    
    const widgetDateFilter = widget?.dateFilter;
    const useWidgetDate = widgetDateFilter?.mode === 'custom' && widgetDateFilter?.start && widgetDateFilter?.end;

    // 1. Check root level preference (legacy)
    const widgetWantsComparison = widget?.useComparison === true;

    // 2. Check individual date filter preference (source of truth atual para CARD)
    const hasWidgetDateFilterPreference = typeof widgetDateFilter?.useComparison === 'boolean';
    const widgetDateFilterWantsComparison = widgetDateFilter?.useComparison === true;

    // 3. Determine if we should use comparison
    const effectiveUseComparison = (useComparison !== undefined) 
        ? useComparison 
        : (hasWidgetDateFilterPreference ? widgetDateFilterWantsComparison : widgetWantsComparison);

    let fStart, fEnd;
    if (useWidgetDate) {
        fStart = normalizeDateStr(widgetDateFilter.start);
        fEnd = normalizeDateStr(widgetDateFilter.end);
    } else {
        fStart = normalizeDateStr(filters.start);
        fEnd = normalizeDateStr(filters.end);
    }

    if (!fStart || !fEnd) {
       // Se ainda assim não tiver datas, usar o global como fallback final
       fStart = normalizeDateStr(filters.start);
       fEnd = normalizeDateStr(filters.end);
    }

    let cStart, cEnd;
    if (widgetDateFilterWantsComparison && widgetDateFilter.comparisonStart && widgetDateFilter.comparisonEnd) {
         // Priority: Custom Widget Comparison Dates
         cStart = normalizeDateStr(widgetDateFilter.comparisonStart);
         cEnd = normalizeDateStr(widgetDateFilter.comparisonEnd);
    } else if (effectiveUseComparison) {
         cStart = normalizeDateStr(comparison?.start);
         cEnd = normalizeDateStr(comparison?.end);
         
         if (!cStart || !cEnd) {
            cStart = dayjs(fStart).subtract(1, 'year').format('YYYY-MM-DD');
            cEnd = dayjs(fEnd).subtract(1, 'year').format('YYYY-MM-DD');
         }
    } else {
         cStart = normalizeDateStr(comparison?.start);
         cEnd = normalizeDateStr(comparison?.end);
    }
 
    if (effectiveUseComparison && (!cStart || !cEnd)) return [];
 
    const from = effectiveUseComparison ? cStart : fStart;
    const to   = effectiveUseComparison ? cEnd   : fEnd;
    const compiledFilters = buildMergedDimensionFiltersPayload(widget?.dimFilters, dimensionFilter, sync?.erp, sync?.type);
    const effectiveRegime = getEffectiveMetricRegime(metric, regime);

    // Retrocompatibilidade: se for número simples, mantém fluxo antigo getVisualData
    const isArrayDims = Array.isArray(dimensionOrDimensions);
    if (!isArrayDims) {
      let dimension = dimensionOrDimensions;
      // Trata métricas de data para datas tipo coringa (retrocompatibilidade)
      if (dimension > 0) {
        const dimensionObj = getDimensionByIdentifier(dimension);
        const metricObj = getMetricByIdentifier(metric);
        if (dimensionObj.isDate || dimensionObj.identifier === 1000 || dimensionObj.identifier === 1001 || dimensionObj.identifier === 1002) {
          const baseDateDimension = getMetricBaseDateDimension(dimensionObj, metricObj);
          if (baseDateDimension) {
            dimension = baseDateDimension;
          }
        }
      }

      const resp = await ReportBuilderService.getVisualData(
        sync?.erp,
        sync?.externalId,
        dimension,
        metric,
        from,
        to,
        compiledFilters,
        effectiveRegime,
        widget?.type
      );
      return resp.data.response;
    }

    // Novo fluxo: múltiplas dimensões para tabela
    // Trata métricas de data para datas tipo coringa também no modo tabela
    let dimensionsArray = Array.isArray(dimensionOrDimensions) ? [...dimensionOrDimensions] : [];
    try {
      const metricObj = getMetricByIdentifier(metric);
      dimensionsArray = dimensionsArray.map((dim) => {
        if (dim > 0) {
          const dimensionObj = getDimensionByIdentifier(dim);
          if (dimensionObj?.isDate || dimensionObj?.identifier === 1000 || dimensionObj?.identifier === 1001 || dimensionObj?.identifier === 1002) {
            const baseDateDimension = getMetricBaseDateDimension(dimensionObj, metricObj);
            if (baseDateDimension) return baseDateDimension;
          }
        }
        return dim;
      });
    } catch (e) {
      // fallback silencioso caso helpers não estejam disponíveis por algum motivo
    }
    const resp = await ReportBuilderService.getTableData(
      sync?.erp,
      sync?.externalId,
      metric,
      dimensionsArray,
      from,
      to,
      compiledFilters,
      effectiveRegime
    );
    // Backend já retorna response com linhas { value, d0..dN }
    return resp.data.response;
  }, [editingWidget, filters.start, filters.end, comparison?.start, comparison?.end, dimensionFilter, sync?.erp, sync?.externalId]);

  const buildVisualDetailBaseRequest = useCallback((metric, dimension, widget = undefined, regime = undefined) => {
    if (!widget && editingWidget) widget = editingWidget;

    const widgetDateFilter = widget?.dateFilter;
    const useWidgetDate = widgetDateFilter?.mode === "custom" && widgetDateFilter?.start && widgetDateFilter?.end;

    let fStart, fEnd;
    if (useWidgetDate) {
      fStart = normalizeDateStr(widgetDateFilter.start);
      fEnd = normalizeDateStr(widgetDateFilter.end);
    } else {
      fStart = normalizeDateStr(filters.start);
      fEnd = normalizeDateStr(filters.end);
    }

    if (!fStart || !fEnd) {
      fStart = normalizeDateStr(filters.start);
      fEnd = normalizeDateStr(filters.end);
    }

    const compiledFilters = buildMergedDimensionFiltersPayload(
      widget?.dimFilters,
      dimensionFilter,
      sync?.erp,
      sync?.type
    );

    let effectiveDimension = dimension;
    try {
      if (effectiveDimension > 0) {
        const dimensionObj = getDimensionByIdentifier(effectiveDimension);
        const metricObj = getMetricByIdentifier(metric);
        if (
          dimensionObj?.isDate ||
          dimensionObj?.identifier === 1000 ||
          dimensionObj?.identifier === 1001 ||
          dimensionObj?.identifier === 1002
        ) {
          const baseDateDimension = getMetricBaseDateDimension(dimensionObj, metricObj);
          if (baseDateDimension) effectiveDimension = baseDateDimension;
        }
      }
    } catch (e) {
    }

    const effectiveRegime = getEffectiveMetricRegime(metric, regime);

    return {
      erp: sync?.erp,
      externalId: sync?.externalId,
      metric,
      dimension: effectiveDimension,
      period: { start: fStart, end: fEnd },
      dimensionFilters: compiledFilters,
      ...(effectiveRegime ? { regime: effectiveRegime } : {}),
    };
  }, [editingWidget, filters.start, filters.end, dimensionFilter, sync?.erp, sync?.externalId, sync?.type]);

  const normalizeClickedIdForDetail = useCallback((dimension, raw) => {
    const value = String(raw ?? "").trim();
    if (!value) return value;

    const dateDimensionTypes = {
      5: "year",
      8: "year",
      11: "year",
      14: "year",
      22: "year",
      6: "monthYear",
      9: "monthYear",
      12: "monthYear",
      15: "monthYear",
      23: "monthYear",
      7: "day",
      10: "day",
      13: "day",
      16: "day",
      24: "day",
      1000: "year",
      1001: "monthYear",
      1002: "day",
    };

    const dateType = dateDimensionTypes[dimension];
    if (!dateType) return value;

    if (dateType === "year") {
      const m = value.match(/(\d{4})/);
      return m ? m[1] : value;
    }

    if (dateType === "monthYear") {
      let m = value.match(/^(\d{4})-(0?[1-9]|1[0-2])$/);
      if (m) return `${m[1]}-${String(m[2]).padStart(2, "0")}`;
      m = value.match(/^(0?[1-9]|1[0-2])\/(\d{4})$/);
      if (m) return `${m[2]}-${String(m[1]).padStart(2, "0")}`;
      m = value.match(/^([A-Za-zÀ-ÿ]{3})\/(\d{4})$/);
      if (m) {
        const monthMap = {
          jan: "01",
          fev: "02",
          mar: "03",
          abr: "04",
          mai: "05",
          jun: "06",
          jul: "07",
          ago: "08",
          set: "09",
          out: "10",
          nov: "11",
          dez: "12",
        };
        const key = m[1]
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .toLowerCase();
        const mm = monthMap[key];
        if (mm) return `${m[2]}-${mm}`;
      }
      return value;
    }

    if (dateType === "day") {
      let m = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (m) return `${m[1]}-${m[2]}-${m[3]}`;
      m = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
      if (m) {
        const dd = String(m[1]).padStart(2, "0");
        const mm = String(m[2]).padStart(2, "0");
        return `${m[3]}-${mm}-${dd}`;
      }
      return value;
    }

    return value;
  }, []);

  const fetchVisualDetailData = useCallback(async (payload, config = undefined) => {
    const resp = await ReportBuilderService.getVisualDetailData(payload, config);
    const root = resp?.data;
    if (root && root.success === false) {
      throw new Error(root?.message || "Detalhamento indisponível.");
    }
    const data = root?.data || root?.response || {};
    return {
      items: data?.items || [],
      page: data?.page,
      pageSize: data?.pageSize,
      total: data?.total,
    };
  }, []);

  const fetchMatrixAPI = useCallback(async (metric, allDimensionIdentifiers, columnDimIndices, widget = undefined, regime = undefined) => {
    if (!widget && editingWidget) widget = editingWidget;
    
    const widgetDateFilter = widget?.dateFilter;
    const useWidgetDate = widgetDateFilter?.mode === 'custom' && widgetDateFilter?.start && widgetDateFilter?.end;

    const widgetWantsComparison = widget?.useComparison === true;
    const widgetDateFilterWantsComparison = widgetDateFilter?.useComparison === true;
    
    const effectiveUseComparison = widgetWantsComparison || widgetDateFilterWantsComparison;
    
    let fStart, fEnd;
    if (useWidgetDate) {
        fStart = normalizeDateStr(widgetDateFilter.start);
        fEnd = normalizeDateStr(widgetDateFilter.end);
    } else {
        fStart = normalizeDateStr(filters.start);
        fEnd = normalizeDateStr(filters.end);
    }

    if (!fStart || !fEnd) {
       fStart = normalizeDateStr(filters.start);
       fEnd = normalizeDateStr(filters.end);
    }

    let cStart, cEnd;
    if (widgetDateFilterWantsComparison && widgetDateFilter.comparisonStart && widgetDateFilter.comparisonEnd) {
         cStart = normalizeDateStr(widgetDateFilter.comparisonStart);
         cEnd = normalizeDateStr(widgetDateFilter.comparisonEnd);
    } else if (effectiveUseComparison) {
         cStart = normalizeDateStr(comparison?.start);
         cEnd = normalizeDateStr(comparison?.end);

         if (!cStart || !cEnd) {
            cStart = dayjs(fStart).subtract(1, 'year').format('YYYY-MM-DD');
            cEnd = dayjs(fEnd).subtract(1, 'year').format('YYYY-MM-DD');
         }
    } else {
         cStart = normalizeDateStr(comparison?.start);
         cEnd = normalizeDateStr(comparison?.end);
    }

    if (effectiveUseComparison && (!cStart || !cEnd)) return [];
    const from = effectiveUseComparison ? cStart : fStart;
    const to   = effectiveUseComparison ? cEnd   : fEnd;

    // Filtros de dimensão mesclados (widget + globais)
    const compiledFilters = buildMergedDimensionFiltersPayload(widget?.dimFilters, dimensionFilter, sync?.erp, sync?.type);

    // Normalização de dimensões (tratar wildcards de data como em fetchAPI)
    const effectiveRegime = getEffectiveMetricRegime(metric, regime);

    let normalizedDims = Array.isArray(allDimensionIdentifiers) ? [...allDimensionIdentifiers] : [];
    try {
      const metricObj = getMetricByIdentifier(metric);
      normalizedDims = normalizedDims.map((dim) => {
        if (dim > 0) {
          const dimObj = getDimensionByIdentifier(dim);
          if (dimObj?.isDate || dimObj?.identifier === 1000 || dimObj?.identifier === 1001 || dimObj?.identifier === 1002) {
            const base = getMetricBaseDateDimension(dimObj, metricObj);
            if (base) return base;
          }
        }
        return dim;
      });
    } catch {}

    // Filtrar dimensões válidas (agora aceita até 4 dimensões)
    const limitedDims = normalizedDims.filter(v => Number.isFinite(v) && v >= 0);


    // Usar o endpoint GetTableData com o payload correto
    const resp = await ReportBuilderService.getTableData(
      sync?.erp,
      sync?.externalId,
      metric,
      limitedDims,
      from,
      to,
      compiledFilters,
      effectiveRegime
    );
    return resp?.data?.response ?? [];
  }, [editingWidget, filters.start, filters.end, comparison?.start, comparison?.end, dimensionFilter, sync?.erp, sync?.externalId]);

  //Função que busca dados para componentes customizados
  const fetchCustomAPI = useCallback(async (widget) => {
    if(!widget && editingWidget) widget = editingWidget
    // console.log("🔄 Buscando dados para widget:", widget);
    const compiledFilters = buildMergedDimensionFiltersPayload(widget?.dimFilters, dimensionFilter, sync?.erp, sync?.type);
    
    const widgetDateFilter = widget?.dateFilter;
    const useWidgetDate = widgetDateFilter?.mode === 'custom' && widgetDateFilter?.start && widgetDateFilter?.end;

    // 2. Check individual date filter preference (source of truth atual para CARD)
    const hasWidgetDateFilterPreference = typeof widgetDateFilter?.useComparison === 'boolean';
    const widgetDateFilterWantsComparison = widgetDateFilter?.useComparison === true;

    // 3. Determine if we should use comparison
    const effectiveUseComparison = hasWidgetDateFilterPreference
        ? widgetDateFilterWantsComparison
        : widget?.useComparison === true;

    let start, end;
    if (useWidgetDate) {
        start = normalizeDateStr(widgetDateFilter.start);
        end = normalizeDateStr(widgetDateFilter.end);
    } else {
        start = normalizeDateStr(filters.start);
        end = normalizeDateStr(filters.end);
    }

    if (!start || !end) {
       start = normalizeDateStr(filters.start);
       end = normalizeDateStr(filters.end);
    }

    let cStart, cEnd;
    if (widgetDateFilterWantsComparison && widgetDateFilter.comparisonStart && widgetDateFilter.comparisonEnd) {
         cStart = normalizeDateStr(widgetDateFilter.comparisonStart);
         cEnd = normalizeDateStr(widgetDateFilter.comparisonEnd);
    } else if (effectiveUseComparison) {
         cStart = normalizeDateStr(comparison?.start);
         cEnd = normalizeDateStr(comparison?.end);

         if (!cStart || !cEnd) {
            cStart = dayjs(start).subtract(1, 'year').format('YYYY-MM-DD');
            cEnd = dayjs(end).subtract(1, 'year').format('YYYY-MM-DD');
         }
    } else {
         cStart = comparison?.start ? normalizeDateStr(comparison?.start) : undefined;
         cEnd = comparison?.end ? normalizeDateStr(comparison?.end) : undefined;
    }

    // Fluxo de Caixa: só envia período de comparação à API se houver intervalo explícito
    // (datas no relatório ou comparisonStart/End no filtro do widget). Sem isso, não
    // usar o fallback de -1 ano — paridade com o DRE (período principal).
    const fluxoCaixaHasExplicitComparison =
      (widgetDateFilterWantsComparison &&
        widgetDateFilter?.comparisonStart &&
        widgetDateFilter?.comparisonEnd) ||
      (Boolean(normalizeDateStr(comparison?.start)) && Boolean(normalizeDateStr(comparison?.end)));
    const fluxoCaixaPeriodStart =
      effectiveUseComparison && fluxoCaixaHasExplicitComparison ? cStart : start;
    const fluxoCaixaPeriodEnd =
      effectiveUseComparison && fluxoCaixaHasExplicitComparison ? cEnd : end;

    switch (widget.type) {
      case WIDGET_TYPES.DRE:
        const config = widget.config || {};
        const regimeType = config.regimeType || 'emissao';
        const dreGroupBy = (typeof config.dreGroupBy === 'number') ? config.dreGroupBy : undefined;
        const resp = await ReportBuilderService.getDREData(
          sync.erp, sync.externalId, 0, 58,
          effectiveUseComparison ? cStart : start,
          effectiveUseComparison ? cEnd : end,
          compiledFilters, regimeType, dreGroupBy
        );    
         return resp.data.response
      case WIDGET_TYPES.ABC:
        const abcResp = await ReportBuilderService.getABCData(
           sync.erp, sync.externalId, widget.dimensions[0].dimensionIdentifier,
           widget.groups, widget.cutA, widget.cutB,
           effectiveUseComparison ? cStart : start,
           effectiveUseComparison ? cEnd : end,
           compiledFilters
         );
        return abcResp.data.response
      case WIDGET_TYPES.FLUXO_CAIXA:
        // Busca dados reais do backend + devolve config de hierarquia/regime
        try {
          const hasCategoriaSuperior = sync?.erp !== undefined && ERPS_COM_CATEGORIA_SUPERIOR.includes(sync?.erp);
          const hierarchyOrder = hasCategoriaSuperior ? ['categoriaSuperior', 'categoria', 'pessoa', 'dia'] : ['categoria', 'pessoa', 'dia'];
          const hierarchyConfig = widget.config?.hierarchy || { hierarchyOrder: hierarchyOrder };
          const regimeType = widget.config?.regimeType || 'pagamento';
          const usaSaldo = widget.config?.usaSaldo || false;
          const compiledFilters = buildMergedDimensionFiltersPayload(widget?.dimFilters, dimensionFilter, sync?.erp, sync?.type);
          const resp = await ReportBuilderService.getFluxoCaixaData(
            sync?.erp,
            sync?.externalId,
            fluxoCaixaPeriodStart,
            fluxoCaixaPeriodEnd,
            compiledFilters,
            regimeType,
            usaSaldo
          );
          const root = resp?.data ?? resp;
          if (root && typeof root === "object" && root.success === false) {
            throw new Error(root?.message || "Falha ao buscar Fluxo de Caixa.");
          }

          const rawResponse = root?.response ?? root;
          const isObj = rawResponse && typeof rawResponse === "object" && !Array.isArray(rawResponse);

          const data = Array.isArray(rawResponse)
            ? rawResponse
            : isObj
              ? (rawResponse?.itens ?? rawResponse?.items ?? [])
              : [];

          const saldoInicialRaw = isObj ? (rawResponse?.saldoInicial ?? rawResponse?.SaldoInicial ?? []) : [];
          const hasSaldoInicial = Array.isArray(saldoInicialRaw);
          const effectiveUsaSaldo = Boolean(usaSaldo) && isObj && hasSaldoInicial;

          return { data, saldoInicial: effectiveUsaSaldo ? saldoInicialRaw : [], usaSaldo: effectiveUsaSaldo, hierarchyConfig, regimeType };
        } catch (e) {
          console.error('Erro ao buscar Fluxo de Caixa:', e);
          return { data: [], saldoInicial: [], usaSaldo: widget.config?.usaSaldo || false, hierarchyConfig: widget.config?.hierarchy || { hierarchyOrder: ['categoria', 'pessoa', 'dia'] }, regimeType: widget.config?.regimeType || 'pagamento' };
        }
      case WIDGET_TYPES.DFC:
        try {
          const regimeType = widget.config?.regimeType || 'pagamento';
          const compiledFilters = buildMergedDimensionFiltersPayload(widget?.dimFilters, dimensionFilter, sync?.erp, sync?.type);
          const dfcResp = await ReportBuilderService.getDFCData(
            sync?.erp,
            sync?.externalId,
            effectiveUseComparison ? cStart : start,
            effectiveUseComparison ? cEnd : end,
            compiledFilters,
            regimeType
          );
          return dfcResp.data.response;
        } catch (e) {
          console.error('Erro ao buscar DFC:', e);
          return { data: [], regimeType: widget.config?.regimeType || 'emissao' };
        }
      case WIDGET_TYPES.SALDOS:
        try {
          const cfg = widget?.config || {};
          const daysForward = Number(cfg.daysForward || 30);
          const includeSolvency = !!cfg.includeSolvency;
          const dateBase = cfg.dateBase || "vencimento";
          const status = Array.isArray(cfg.status) ? cfg.status : [];
          const viewMode = cfg.viewMode || "table";
          const resp = await ReportBuilderService.getSaldosData(
            sync?.erp,
            sync?.externalId,
            compiledFilters,
            daysForward,
            includeSolvency,
            dateBase,
            status
          );
          const raw = resp?.data?.response ?? {};
          return {
            ...raw,
            viewMode,
            daysForward,
            includeSolvency,
            dateBase,
            status,
          };
        } catch (e) {
          console.error("Erro ao buscar Saldos:", e);
          return {
            daysForward: widget?.config?.daysForward || 30,
            includeSolvency: !!widget?.config?.includeSolvency,
            dateBase: widget?.config?.dateBase || "vencimento",
            status: Array.isArray(widget?.config?.status) ? widget.config.status : [],
            viewMode: widget?.config?.viewMode || "table",
            itens: [],
            grafico: [],
            saldoAtualTotal: 0,
            saldoInicialPorConta: []
          };
        }
      default:
        return []
    }
  }, [editingWidget, filters.start, filters.end, comparison?.start, comparison?.end, dimensionFilter, sync?.erp, sync?.externalId]);

  //==============================
  // Seção de filtro de dimensão
  //==============================
  const deepClone = (obj) => (obj ? JSON.parse(JSON.stringify(obj)) : obj);

  // 👉 reidrata o editingWidget com campos salvos no store (ex.: dimFilters)
  useEffect(() => {
    if (!editingWidget) return;
    const src = widgets.find(w => w.id === editingWidget.id);
    if (!src) return;
  
    // Campos que queremos garantir no editor
    const ensureKeys = ['dimFilters', 'dateFilter'];
  
    let needsUpdate = false;
    const merged = { ...editingWidget };
  
    ensureKeys.forEach((k) => {
      const hasInEditing = merged[k] !== undefined && merged[k] !== null;
      const isEmptyObj = hasInEditing && typeof merged[k] === 'object' && Object.keys(merged[k]).length === 0;
      const hasInSrc     = src[k]     !== undefined && src[k]     !== null;
      if ((!hasInEditing || isEmptyObj) && hasInSrc) {
        merged[k] = deepClone(src[k]);
        needsUpdate = true;
      }
    });
  
    if (needsUpdate) setEditingWidget(merged);
  }, [editingWidget?.id, widgets]);

  // 👉 reidrata o filteringWidget
  useEffect(() => {
    if (!filteringWidget) return;
    const src = widgets.find(w => w.id === filteringWidget.id);
    if (!src) return;
  
    const ensureKeys = ['dimFilters', 'dateFilter'];
  
    let needsUpdate = false;
    const merged = { ...filteringWidget };
  
    ensureKeys.forEach((k) => {
      const hasInFiltering = merged[k] !== undefined && merged[k] !== null;
      // Removida a verificação de objeto vazio para permitir que o estado "vazio" (mas existente no source) seja carregado corretamente.
      // Antes: const isEmptyObj = hasInFiltering && typeof merged[k] === 'object' && Object.keys(merged[k]).length === 0;
      // O problema é que se o filtro no widget for { categoria: { mode: 1, values: [...] } }, e no filteringWidget estiver {},
      // ele até atualizava. Mas se no filteringWidget estivesse undefined, ele atualizava.
      // O bug descrito sugere que ao abrir, ele pode estar resetando ou não carregando o que já existe.
      
      const hasInSrc = src[k] !== undefined && src[k] !== null;
      
      // Se a propriedade existe no source, mas não no filteringWidget (ou está vazia/default), trazemos do source.
      // Mas cuidado: se o usuário limpou o filtro, o filteringWidget pode ter {} intencionalmente.
      // Porém, este useEffect roda quando filteringWidget muda (ex: ao abrir o modal).
      // Se ao abrir o modal, filteringWidget vem "cru" (só com ID e tipo), precisamos popular.
      
      // Vamos simplificar: SEMPRE que o filteringWidget mudar (abrir), se ele não tiver a propriedade ou ela estiver vazia,
      // tentamos pegar do widget original salvo (src).
      // A condição `!hasInFiltering` cobre "undefined".
      // A condição `isEmptyObj` cobre "{}".
      
      const isEmptyObj = hasInFiltering && typeof merged[k] === 'object' && Object.keys(merged[k]).length === 0;

      if ((!hasInFiltering || isEmptyObj) && hasInSrc) {
        merged[k] = deepClone(src[k]);
        needsUpdate = true;
      }
    });
  
    if (needsUpdate) setFilteringWidget(merged);
  }, [filteringWidget?.id, widgets]);
  
  //==============================
  // Fim da seção de filtro de dimensão
  //==============================

  const contextValue = useMemo(() => ({
    layout,
    setLayout,
    widgets,
    setWidgets,
    addWidget,
    removeWidget,
    editingWidget,
    setEditingWidget,
    updateWidget,
    filteringWidget,
    setFilteringWidget,
    updateFilteringWidget,
    metrics,
    setMetrics,
    dimensions,
    setDimensions,
    filters,
    setFilters,
    sync,
    setSync,
    isPreview,
    setIsPreview,
    duplicateWidget,
    title,
    setTitle,
    description,
    setDescription,
    template,
    setTemplate,
    loadTemplate,
    comparison,
    setComparison,
    fetchAPI,
    buildVisualDetailBaseRequest,
    normalizeClickedIdForDetail,
    fetchVisualDetailData,
    fetchMatrixAPI,
    saveReport,
    updateWidgetData,
    resetState,
    reportId,
    setReportId,
    reportType,
    setReportType,
    loading,
    setLoading,
    calculateGridHeight,
    makeResponsiveLayouts,
    fetchCustomAPI,
    getDimensionOptionsForMetrics,
    widgetData,
    isDirty,
    markSaved,
    useComparisonMode, setUseComparisonMode,
    readOnly, setReadOnly,
    dimensionFilter, setDimensionFilter,
    dimensionValuesCache, setDimensionValuesCache,
    gridHeight
  }), [
    layout, widgets, editingWidget, filteringWidget, metrics, dimensions, filters, sync, isPreview, title, description,
    template, comparison, reportId, reportType, loading, widgetData, isDirty, useComparisonMode,
    readOnly, dimensionFilter, dimensionValuesCache, gridHeight,
    // Memoized functions
    addWidget, removeWidget, duplicateWidget, updateWidget, updateFilteringWidget, fetchAPI, buildVisualDetailBaseRequest, normalizeClickedIdForDetail, fetchVisualDetailData, fetchMatrixAPI, fetchCustomAPI,
    saveReport, updateWidgetData, resetState, calculateGridHeight, makeResponsiveLayouts,
    loadTemplate, markSaved, getDimensionOptionsForMetrics
  ]);

  return (
    <ReportBuilderContext.Provider value={contextValue}>
      {children}
    </ReportBuilderContext.Provider>
  );
};

export const useReportBuilder = () => useContext(ReportBuilderContext);
